import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  updateCustomerThunk,
  useAccountToDisplaySelector
} from "../../redux/actions/user";
import { useTrackingIdRouting } from "../../utils/routing/routingUtils";

import KC_Button, {
  KC_ButtonSize
} from "../../designsystem/KC_Button/KC_Button";
import { tt } from "../../utils/i18n/i18n";
import { updateCustomer } from "../13_SaaS/api";
import { type Config } from "kadow-jetpack-model";
import {
  BACKGROUND,
  DESCRIPTION,
  GENERATE_GIFT_WORDING,
  JETPACK_ONBOARDING_STEPS,
  TITLE
} from "./constants";
import GamesSelectorOnboarding from "./components/GamesSelector/GamesSelectorOnboarding";
import CTAsSelectorOnboarding from "./components/CTAsSelectorOnboarding";
import KC_OnboardingStepper from "../../designsystem/KC_OnboardingStepper/KC_OnboardingStepper";
import OnboardingContainer from "./components/OnboardingContainer";
import GiftSelector04Onboarding from "./components/GiftSelector/GiftSelector04Onboarding";
import LastStep from "./components/LastStep";
import OnboardingFinishedRocketAnimation from "./components/TransitionPage/TransitionPage";
import FireworksBackground from "../../components/Firework/FireworkBackground";
import scrollToTop from "../../utils/helpers/scrollToTop";
import { BASE_URL } from "../../utils/constants";
import { useSubscriptionPlansSelector } from "../../redux/actions/app";
import OnboardingForm from "./components/OnboardingForm";
import { BusinessUserProfileForm } from "@tootsweet/model/entities/BusinessUserProfileForm";
import { useDispatch } from "react-redux";

export interface StepProps {
  nextStep: (config?: Config) => void;
  previousStep: () => void;
  goToStep?: (step: string) => void;
}

export default function JetpackOnboarding() {
  // Apply routing guard to validate trackingID
  useTrackingIdRouting("JetpackOnboarding");

  const dispatch = useDispatch();

  const { accountToDisplay } = useAccountToDisplaySelector();
  const allSubscriptionPlans = useSubscriptionPlansSelector();

  const [currentStep, setCurrentStep] = useState<string>("");

  useEffect(() => {
    if (currentStep !== "") {
      return;
    }

    if (!!accountToDisplay?.businessUser?.onboardingStep) {
      setCurrentStep(accountToDisplay.businessUser.onboardingStep);
      return;
    }

    setCurrentStep(
      accountToDisplay?.businessUser?.parentAccount
        ? JETPACK_ONBOARDING_STEPS.STEP_0_INFO
        : JETPACK_ONBOARDING_STEPS.STEP_1_GAMES
    );
  }, [currentStep, accountToDisplay]);

  const isLoyaltyPlanRef = useRef(false);

  useEffect(() => {
    if (!accountToDisplay?.saasPlanId || !allSubscriptionPlans) {
      isLoyaltyPlanRef.current = false;
      return;
    }

    const currentPlan = allSubscriptionPlans.find(
      (plan) => plan._id === accountToDisplay?.saasPlanId
    );

    isLoyaltyPlanRef.current = !!currentPlan?.isLoyalty;
  }, [accountToDisplay, allSubscriptionPlans]);

  const updateOnboardingStep = useCallback(
    async (step: string) => {
      await updateCustomer(
        { onboardingStep: step },
        accountToDisplay?.trackingID,
        accountToDisplay?.token
      );
    },
    [accountToDisplay?.trackingID, accountToDisplay?.token]
  );

  const goToNextStep = useCallback(async () => {
    const steps = Object.values(JETPACK_ONBOARDING_STEPS);

    const currentIndex = steps.indexOf(currentStep);

    if (currentIndex < steps.length - 1) {
      let nextStepIndex = currentIndex + 1;
      let nextStep = steps[nextStepIndex];

      /**
       * Do not display the CTA step for loyalty plans
       */
      if (
        isLoyaltyPlanRef.current &&
        currentStep === JETPACK_ONBOARDING_STEPS.STEP_1_GAMES &&
        nextStep === JETPACK_ONBOARDING_STEPS.STEP_2_CTAs
      ) {
        nextStep = JETPACK_ONBOARDING_STEPS.STEP_3_GIFTS;
      }

      setCurrentStep(nextStep);
      await updateOnboardingStep(nextStep);
    }
  }, [currentStep, updateOnboardingStep]);

  const goToPreviousStep = useCallback(async () => {
    const steps = Object.values(JETPACK_ONBOARDING_STEPS);

    const currentIndex = steps.indexOf(currentStep);

    if (currentIndex > 0) {
      let prevStepIndex = currentIndex - 1;
      let prevStep = steps[prevStepIndex];

      if (
        isLoyaltyPlanRef.current &&
        currentStep === JETPACK_ONBOARDING_STEPS.STEP_3_GIFTS &&
        prevStep === JETPACK_ONBOARDING_STEPS.STEP_2_CTAs
      ) {
        prevStep = JETPACK_ONBOARDING_STEPS.STEP_1_GAMES;
      }

      setCurrentStep(prevStep);
      await updateOnboardingStep(prevStep);
    }
  }, [currentStep, updateOnboardingStep]);

  const onSubmit = async (values: BusinessUserProfileForm) => {
    if (!accountToDisplay?.trackingID || !accountToDisplay?.token) {
      alert(tt("error"));
      return;
    }

    dispatch(
      updateCustomerThunk(
        {
          customerType: values.customerType,
          placeName: values.placeName,
          contactName: values.contactName,
          contactFirstName: values.contactFirstName,
          contactLastName: values.contactLastName,
          contactPhoneNumber: values.contactPhoneNumber,
          shippingAddress: values.shippingAddress,
          avgBasket: values.avgBasket,
          currency: values.currency,
          dCurrency: values.dCurrency,
          clientsPerDay: values.clientsPerDay
        },
        accountToDisplay.trackingID,
        accountToDisplay.token
      )
    );
    await goToNextStep();
  };

  const CHILDREN = useMemo(() => {
    switch (currentStep) {
      case JETPACK_ONBOARDING_STEPS.STEP_0_INFO:
        if (!accountToDisplay?.businessUser) {
          return null;
        }
        return (
          <div className="px-4 onboarding-info-form">
            <OnboardingForm
              initialValues={accountToDisplay.businessUser}
              onSubmit={onSubmit}
            />
          </div>
        );

      case JETPACK_ONBOARDING_STEPS.STEP_1_GAMES:
        return (
          <GamesSelectorOnboarding
            nextStep={goToNextStep}
            previousStep={goToPreviousStep}
          />
        );

      case JETPACK_ONBOARDING_STEPS.STEP_2_CTAs:
        return (
          <CTAsSelectorOnboarding
            nextStep={goToNextStep}
            previousStep={goToPreviousStep}
          />
        );

      case JETPACK_ONBOARDING_STEPS.STEP_3_GIFTS:
        return (
          <GiftSelector04Onboarding
            nextStep={goToNextStep}
            previousStep={goToPreviousStep}
          />
        );

      case JETPACK_ONBOARDING_STEPS.STEP_4_LAST_STEP:
        return (
          <LastStep nextStep={goToNextStep} previousStep={goToPreviousStep} />
        );

      case JETPACK_ONBOARDING_STEPS.STEP_5_ROCKET_ANIMATION:
        return <OnboardingFinishedRocketAnimation />;

      default:
        return null;
    }
  }, [accountToDisplay, currentStep, goToNextStep, goToPreviousStep]);

  const showPreviousButton = useMemo(() => {
    if (
      accountToDisplay?.businessUser?.parentAccount &&
      currentStep === JETPACK_ONBOARDING_STEPS.STEP_0_INFO
    ) {
      return false;
    }

    if (
      !accountToDisplay?.businessUser?.parentAccount &&
      currentStep === JETPACK_ONBOARDING_STEPS.STEP_1_GAMES
    ) {
      return false;
    }

    return true;
  }, [currentStep, accountToDisplay]);

  const hideStepper = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("so") === "true";
  }, [location.search]);

  const HEADER = useCallback(() => {
    return (
      <div className="h-100 d-flex justify-content-between align-items-center mx-4">
        {showPreviousButton && (
          <KC_Button
            extraStyle={{ width: 110 }}
            isInverted
            size={KC_ButtonSize.Giant}
            text={`← ${tt("jp_return_btn_text")}`}
            className="pl-0 text-left"
            onClick={goToPreviousStep}
          />
        )}
        {!hideStepper && (
          <KC_Button
            id="skip-step-btn"
            isInverted
            size={KC_ButtonSize.Medium}
            text={tt("jp_skip_btn_text")}
            className="pl-4 text-right text-sm-right d-flex ml-auto w-auto skip-btn"
            onClick={async () => {
              await goToNextStep();
            }}
          />
        )}
      </div>
    );
  }, [
    showPreviousButton,
    currentStep,
    goToNextStep,
    goToPreviousStep,
    hideStepper
  ]);

  const pcCompleted = useMemo(() => {
    if (isLoyaltyPlanRef.current) {
      switch (currentStep) {
        case JETPACK_ONBOARDING_STEPS.STEP_1_GAMES:
          return 0.25;
        case JETPACK_ONBOARDING_STEPS.STEP_3_GIFTS:
          return 0.75;
        case JETPACK_ONBOARDING_STEPS.STEP_4_LAST_STEP:
          return 1;
        case JETPACK_ONBOARDING_STEPS.STEP_5_ROCKET_ANIMATION:
          return 1;
        default:
          return 0;
      }
    }

    switch (currentStep) {
      case JETPACK_ONBOARDING_STEPS.STEP_1_GAMES:
        return 0.1;
      case JETPACK_ONBOARDING_STEPS.STEP_2_CTAs:
        return 0.2;
      // case JETPACK_ONBOARDING_STEPS.STEP_4:
      //   return 0.4
      // case JETPACK_ONBOARDING_STEPS.STEP_5:
      //   return 0.5
      // case JETPACK_ONBOARDING_STEPS.STEP_5_1:
      //   return 0.55
      // case JETPACK_ONBOARDING_STEPS.STEP_6:
      //   return 0.6
      // case STEPS.STEP_7:
      //   return 0.7
      case JETPACK_ONBOARDING_STEPS.STEP_3_GIFTS:
        return 0.8;
      case JETPACK_ONBOARDING_STEPS.STEP_4_LAST_STEP:
        return 1;
      case JETPACK_ONBOARDING_STEPS.STEP_5_ROCKET_ANIMATION:
        return 1;
      default:
        return 0;
    }
  }, [currentStep]);

  const showHeader = useMemo(() => {
    return (
      currentStep !== JETPACK_ONBOARDING_STEPS.STEP_5_ROCKET_ANIMATION &&
      currentStep !== JETPACK_ONBOARDING_STEPS.STEP_4_LAST_STEP
    );
  }, [currentStep]);

  const isGeneratePrizesTrackerFound = useMemo(() => {
    const isGeneratePrizes = localStorage.getItem("generate-prizes");
    return !!isGeneratePrizes;
  }, [currentStep]);

  const useBackground = useMemo(() => {
    return currentStep !== JETPACK_ONBOARDING_STEPS.STEP_5_ROCKET_ANIMATION;
  }, [currentStep]);

  useEffect(() => {
    scrollToTop();
  }, [currentStep]);

  return (
    <div id="onboarding" className="onboarding">
      {showHeader && (
        <div className="onboarding-header">
          <HEADER />
        </div>
      )}
      {!hideStepper && pcCompleted < 1 && (
        <KC_OnboardingStepper pcCompleted={pcCompleted} />
      )}
      {currentStep && (
        <OnboardingContainer
          key={currentStep}
          onboardingCompletion={
            (Object.values(JETPACK_ONBOARDING_STEPS).indexOf(currentStep) + 1) *
            0.2
          }
          children={CHILDREN}
          title={
            isGeneratePrizesTrackerFound
              ? GENERATE_GIFT_WORDING
              : TITLE[currentStep]
          }
          description={DESCRIPTION[currentStep]}
          bgImg={`${BASE_URL}/${BACKGROUND[currentStep]}`}
          useBackground={useBackground}
        />
      )}
      {currentStep === JETPACK_ONBOARDING_STEPS.STEP_4_LAST_STEP && (
        <FireworksBackground />
      )}
    </div>
  );
}
